import {
  createItemAuthorityOperationId,
  itemAuthorityService,
  MAX_ITEM_SUMMARY_LENGTH,
} from "@/services/itemAuthorityService";
import {
  createStorageAuthorityOperationId,
  describeStorageAuthorityFile,
  storageAuthorityService,
} from "@/services/storageAuthorityService";

const MAX_MEDIA_ASSETS = 8;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const ATOMIC_AMOUNT_PATTERN = /^[1-9][0-9]{0,39}$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;

export class ItemPublishingAuthorityServiceError extends Error {
  constructor(code = "unknown") {
    super("The item could not be published.");
    this.name = "ItemPublishingAuthorityServiceError";
    this.code = code;
  }
}

function fail(code) {
  throw new ItemPublishingAuthorityServiceError(code);
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function requireExactKeys(value, expectedKeys) {
  if (!isPlainRecord(value)) fail("invalid-argument");
  const actual = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const expected = [...expectedKeys].sort();
  const strings = actual.filter((key) => typeof key === "string").sort();
  if (
    actual.some(
      (key) =>
        typeof key !== "string" ||
        descriptors[key]?.enumerable !== true ||
        !Object.prototype.hasOwnProperty.call(descriptors[key], "value")
    ) ||
    strings.length !== expected.length ||
    strings.some((key, index) => key !== expected[index])
  ) {
    fail("invalid-argument");
  }
  return value;
}

function normalizeOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument");
  }
  return value;
}

function normalizeOperations(value, fileCount) {
  requireExactKeys(value, [
    "createItem",
    "updateItem",
    "publish",
    "list",
    "uploads",
  ]);
  if (!Array.isArray(value.uploads) || value.uploads.length !== fileCount) {
    fail("invalid-argument");
  }
  const uploads = value.uploads.map((entry) => {
    requireExactKeys(entry, ["create", "finalize"]);
    return Object.freeze({
      create: normalizeOperationId(entry.create),
      finalize: normalizeOperationId(entry.finalize),
    });
  });
  const normalized = Object.freeze({
    createItem: normalizeOperationId(value.createItem),
    updateItem: normalizeOperationId(value.updateItem),
    publish: normalizeOperationId(value.publish),
    list: normalizeOperationId(value.list),
    uploads: Object.freeze(uploads),
  });
  const allIds = [
    normalized.createItem,
    normalized.updateItem,
    normalized.publish,
    normalized.list,
    ...uploads.flatMap(({ create, finalize }) => [create, finalize]),
  ];
  if (new Set(allIds).size !== allIds.length) fail("invalid-argument");
  return normalized;
}

function normalizeText(value, { minimum = 0, maximum }) {
  if (typeof value !== "string") fail("invalid-argument");
  const normalized = value.trim();
  if (
    normalized.length < minimum ||
    normalized.length > maximum ||
    CONTROL_CHARACTERS.test(normalized)
  ) {
    fail("invalid-argument");
  }
  return normalized;
}

function normalizeDraft(value) {
  requireExactKeys(value, [
    "kind",
    "title",
    "summary",
    "orgId",
    "audience",
    "priceAtomic",
    "availableStock",
    "files",
    "operations",
  ]);
  if (
    !["product", "service"].includes(value.kind) ||
    typeof value.orgId !== "string" ||
    !ORG_ID_PATTERN.test(value.orgId) ||
    !["organisation", "public"].includes(value.audience) ||
    typeof value.priceAtomic !== "string" ||
    !ATOMIC_AMOUNT_PATTERN.test(value.priceAtomic) ||
    !Number.isSafeInteger(value.availableStock) ||
    value.availableStock < 1 ||
    value.availableStock > 1_000_000_000 ||
    !Array.isArray(value.files) ||
    value.files.length > MAX_MEDIA_ASSETS
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    kind: value.kind,
    title: normalizeText(value.title, { minimum: 1, maximum: 120 }),
    summary: normalizeText(value.summary, { maximum: MAX_ITEM_SUMMARY_LENGTH }),
    orgId: value.orgId,
    audience: value.audience,
    priceAtomic: value.priceAtomic,
    availableStock: value.availableStock,
    files: Object.freeze([...value.files]),
    operations: normalizeOperations(value.operations, value.files.length),
  });
}

function normalizeWorkflowError(error) {
  if (error instanceof ItemPublishingAuthorityServiceError) return error;
  return new ItemPublishingAuthorityServiceError(
    String(error?.code || "unknown")
      .split("/")
      .pop()
  );
}

export function createItemPublishingOperationBundle(
  fileCount,
  cryptoProvider = globalThis.crypto
) {
  if (
    !Number.isSafeInteger(fileCount) ||
    fileCount < 0 ||
    fileCount > MAX_MEDIA_ASSETS
  ) {
    fail("invalid-argument");
  }
  const bundle = {
    createItem: createItemAuthorityOperationId(cryptoProvider),
    updateItem: createItemAuthorityOperationId(cryptoProvider),
    publish: createItemAuthorityOperationId(cryptoProvider),
    list: createItemAuthorityOperationId(cryptoProvider),
    uploads: Array.from({ length: fileCount }, () => ({
      create: createStorageAuthorityOperationId(null, cryptoProvider),
      finalize: createStorageAuthorityOperationId(null, cryptoProvider),
    })),
  };
  return normalizeOperations(bundle, fileCount);
}

export function createItemPublishingAuthorityService({
  items = itemAuthorityService,
  assets = storageAuthorityService,
  describeFile = describeStorageAuthorityFile,
} = {}) {
  if (
    !items ||
    typeof items.createOwnItem !== "function" ||
    typeof items.updateOwnItem !== "function" ||
    typeof items.mutateOwnPlacements !== "function" ||
    !assets ||
    typeof assets.createUploadIntent !== "function" ||
    typeof assets.putUploadFile !== "function" ||
    typeof assets.finalizeUpload !== "function" ||
    typeof describeFile !== "function"
  ) {
    throw new TypeError(
      "Item publishing requires item and asset authority services."
    );
  }
  const completedUploads = new Map();

  return Object.freeze({
    async publishNewItem(draftValue) {
      const draft = normalizeDraft(draftValue);
      try {
        const created = await items.createOwnItem({
          operationId: draft.operations.createItem,
          item: {
            kind: draft.kind,
            title: draft.title,
            summary: draft.summary,
            mediaAssetIds: [],
            originOrgId: draft.orgId,
          },
        });

        const mediaAssetIds = [];
        for (let index = 0; index < draft.files.length; index += 1) {
          const file = draft.files[index];
          const uploadOperations = draft.operations.uploads[index];
          const uploadKey = `${uploadOperations.create}:${uploadOperations.finalize}`;
          const completedUpload = completedUploads.get(uploadKey);
          if (completedUpload) {
            if (
              completedUpload.file !== file ||
              completedUpload.itemId !== created.itemId
            ) {
              fail("aborted");
            }
            mediaAssetIds.push(completedUpload.assetId);
            continue;
          }
          const descriptor = await describeFile(file, "item-media");
          const grant = await assets.createUploadIntent({
            operationId: uploadOperations.create,
            expectedRevision: 0,
            resource: { kind: "item-media", resourceId: created.itemId },
            // The item is not published yet. Current-resource authorization
            // therefore requires a private upload; download visibility will be
            // derived later from the canonical publication.
            audience: "private",
            file: descriptor,
          });
          await assets.putUploadFile(grant, file);
          const finalized = await assets.finalizeUpload({
            operationId: uploadOperations.finalize,
            expectedRevision: 0,
            uploadId: grant.uploadId,
          });
          completedUploads.set(
            uploadKey,
            Object.freeze({
              assetId: finalized.assetId,
              file,
              itemId: created.itemId,
            })
          );
          mediaAssetIds.push(finalized.assetId);
        }

        const updated = await items.updateOwnItem({
          operationId: draft.operations.updateItem,
          expectedRevision: created.revision,
          itemId: created.itemId,
          item: {
            kind: draft.kind,
            title: draft.title,
            summary: draft.summary,
            mediaAssetIds,
            originOrgId: draft.orgId,
          },
        });
        const placement = (action, operationId, expectedRevision, intent) =>
          items.mutateOwnPlacements({
            operationId,
            expectedRevision: [
              {
                itemId: created.itemId,
                publication: expectedRevision.publication,
                listing: expectedRevision.listing,
              },
            ],
            orgId: draft.orgId,
            action,
            placements: [
              {
                itemId: created.itemId,
                audience: intent.audience,
                priceAtomic: intent.priceAtomic,
                availableStock: intent.availableStock,
              },
            ],
          });

        const published = await placement(
          "publish",
          draft.operations.publish,
          { publication: 0, listing: 0 },
          { audience: draft.audience, priceAtomic: null, availableStock: null }
        );
        const publishedRevision = published.results[0];
        if (
          publishedRevision.status === "not-eligible" ||
          publishedRevision.publicationRevision === null ||
          publishedRevision.listingRevision === null
        ) {
          fail("failed-precondition");
        }

        const listed = await placement(
          "list",
          draft.operations.list,
          {
            publication: publishedRevision.publicationRevision,
            listing: publishedRevision.listingRevision,
          },
          {
            audience: null,
            priceAtomic: draft.priceAtomic,
            availableStock: draft.availableStock,
          }
        );
        const listedRevision = listed.results[0];
        if (
          listedRevision.status === "not-eligible" ||
          listedRevision.publicationRevision === null ||
          listedRevision.listingRevision === null
        ) {
          fail("failed-precondition");
        }

        return Object.freeze({
          schemaVersion: 1,
          itemId: created.itemId,
          itemRevision: updated.revision,
          mediaAssetIds: Object.freeze([...mediaAssetIds]),
          publicationRevision: listedRevision.publicationRevision,
          listingRevision: listedRevision.listingRevision,
          outcome: "published",
        });
      } catch (error) {
        throw normalizeWorkflowError(error);
      }
    },
  });
}

export const itemPublishingAuthorityService =
  createItemPublishingAuthorityService();
